import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

const KEY = (pid: string) => ['projects', pid, 'arbeitspakete'] as const;
const DEPS_KEY = (pid: string) => ['projects', pid, 'arbeitspakete', 'dependencies'] as const;
const WBS_KEY = (pid: string) => ['projects', pid, 'wbs-dictionary'] as const;

export function useArbeitspakete(projectId: string | undefined) {
  return useQuery({
    queryKey: KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/arbeitspakete`),
    enabled: !!projectId,
  });
}

/**
 * PHQ-86: Liste inkl. gelöschter Arbeitspakete (erkennbar an `deleted_at`).
 * Eigener Cache-Eintrag, damit Gantt/Zählung/Nummernvergabe die gelöschten nicht sehen.
 * Braucht das Recht arbeitspaket.delete (sonst 403); `enabled` steuert, ob geladen wird.
 */
export function useDeletedArbeitspakete(projectId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...KEY(projectId ?? ''), 'deleted'] as const,
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/arbeitspakete`, { include_deleted: 1, limit: 500 }),
    enabled: !!projectId && enabled,
  });
}

/** PHQ-86: gelöschtes Arbeitspaket wiederherstellen (409: Nummer vergeben / Eltern gelöscht / nicht gelöscht). */
export function useRestoreArbeitspaket(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post(`/api/v1/projects/${pid}/arbeitspakete/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: WBS_KEY(pid) });
      // Kanban-Boards des wiederhergestellten AP sind wieder erreichbar -> Projekt-Aggregat neu laden.
      qc.invalidateQueries({ queryKey: ['projects', pid, 'kanban'] });
    },
  });
}

export function useCreateArbeitspaket(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`/api/v1/projects/${pid}/arbeitspakete`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: WBS_KEY(pid) });
    },
  });
}

export function useUpdateArbeitspaket(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/api/v1/projects/${pid}/arbeitspakete/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: WBS_KEY(pid) });
    },
  });
}

export function useDeleteArbeitspaket(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/projects/${pid}/arbeitspakete/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(pid) });
      qc.invalidateQueries({ queryKey: WBS_KEY(pid) });
    },
  });
}

export function useApDependencies(projectId: string | undefined) {
  return useQuery({
    queryKey: DEPS_KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/arbeitspakete/dependencies`),
    enabled: !!projectId,
  });
}

export function useAddApDependency(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`/api/v1/projects/${pid}/arbeitspakete/dependencies`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEPS_KEY(pid) }),
  });
}

export function useRemoveApDependency(pid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fromId, toId }: { fromId: string; toId: string }) =>
      apiClient.delete(`/api/v1/projects/${pid}/arbeitspakete/dependencies/${fromId}/${toId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: DEPS_KEY(pid) }),
  });
}

export function useWbsDictionary(projectId: string | undefined) {
  return useQuery({
    queryKey: WBS_KEY(projectId ?? ''),
    queryFn: () => apiClient.get(`/api/v1/projects/${projectId}/wbs-dictionary`),
    enabled: !!projectId,
  });
}
